import moment from "moment";
import { api } from "common";
import { Column } from "material-table";
import { useState, useEffect } from "react";
import { IUserType } from "common/src/types";
import { useTranslation } from "react-i18next";
import LockIcon from "@mui/icons-material/Lock";
import BlockIcon from "@mui/icons-material/Block";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useSelector, useDispatch } from "react-redux";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ImageColumn from "components/CustomTable/Column/ImageColumn";
import { CustomMaterialTable } from "components/CustomMaterialTable";
import { RequestPushMsg } from "common/src/other/NotificationFunctions";
import { ThemedDriverActivePlan } from "components/modal/DriverActivePlan";
import { UserAccountStatusModal } from "components/modal/UserAccountStatusModal";

export default function Drivers() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { updateUserCar, fetchUsersOnce } = api;
  const [data, setData] = useState<IUserType[]>();
  const auth = useSelector((state) => state.auth);
  const usersdata = useSelector((state) => state.usersdata);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const settings = useSelector((state) => state.settingsdata.settings);
  const [activatePlanModalOpen, setActivatePlanModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUserType | undefined>(undefined);

  useEffect(() => {
    if (usersdata.staticusers) {
      setData(
        usersdata.staticusers.filter(
          (user) =>
            user.usertype === "driver" && user.firstName && user.email
        )
      );
    }
  }, [usersdata.staticusers]);


  if (!settings || !auth || !auth?.profile) return;

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  const handleOpenAccountModal = (user: IUserType) => {
    setSelectedUser(user);
    setAccountModalOpen(true);
  };

  const handleOpenPlanModal = (user: IUserType) => {
    setSelectedUser(user);
    setActivatePlanModalOpen(true);
  };


  const columns: Column<IUserType>[] = [
    {
      field: "queue",
      type: "boolean",
      title: t("queue"),
    },
    {
      type: "boolean",
      editable: "never",
      field: "carApproved",
      title: t("car_approval"),
    },
    {
      editable: "never",
      field: "createdAt",
      defaultSort: "desc",
      title: t("createdAt"),
      render: (rowData) => rowData.createdAt ? moment(rowData.createdAt).format('DD/MM/YYYY HH:mm') : t("NOT_INFORMED"),
    },
    {
      field: "firstName",
      title: t("first_name"),
    },
    {
      field: "lastName",
      title: t("last_name"),
    },
    {
      field: "mobile",
      title: t("mobile"),
      render: (rowData) => rowData.mobile ? rowData.mobile : t("NOT_INFORMED"),
    },
    {
      field: "email",
      title: t("email"),
      headerStyle: { textAlign: "center" },
      render: (rowData) => rowData.email ? rowData.email : t("NOT_INFORMED"),
    },
    {
      field: "profile_image",
      title: t("profile_image"),
      render: (rowData) =>
        <ImageColumn
          type="avatar"
          altText="profile_image"
          imageUrl={rowData.profile_image}
        />
    },
    {
      field: "verifyId",
      title: t("verify_id"),
      render: (rowData) => rowData.verifyId ? rowData.verifyId : t("NOT_INFORMED"),
    },
    {
      field: "verifyIdImageSelfie",
      title: t("selfie_with_document"),
      hidden: !settings.imageIdApproval,
      cellStyle: { textAlign: "center" },
      render: (rowData) =>
        <ImageColumn
          imageUrl={rowData.verifyIdImageSelfie}
          altText="verifyIdImageSelfie"
        />
    },
    {
      field: "licenseImage",
      title: t("license_image_front"),
      cellStyle: { textAlign: "center" },
      hidden: !settings.license_image_required,
      render: (rowData) =>
        <ImageColumn
          imageUrl={rowData.licenseImage}
          altText="licenseImage"
        />
    },
    {
      field: "licenseImageBack",
      title: t("license_image_back"),
      cellStyle: { textAlign: "center" },
      hidden: !settings.license_image_required,
      render: (rowData) =>
        <ImageColumn
          imageUrl={rowData.licenseImageBack}
          altText="licenseImageBack"
        />
    },
    {
      type: "numeric",
      editable: "never",
      field: "walletBalance",
      title: t("wallet_balance"),
    },
    {
      title: t("you_rated_text"),
      render: (rowData) => <span>{rowData.rating ? rowData.rating : "0"}</span>,
    },
    {
      editable: "never",
      field: "referralId",
      title: t("referralId"),
    },
  ];

  return (
    <>
      <CustomMaterialTable
        title={"drivers"}
        columns={columns}
        data={data ?? []}
        isLoading={usersdata.loading}
        options={{
          rowStyle: (rowData) => {
            if (!rowData.approved && settings.driver_approval) {
              return {
                filter: "drop-shadow(0 0 10px red)",
              };
            }
            return {};
          },
        }}
        actions={[
          (rowData: IUserType) => ({
            onClick: () => handleOpenAccountModal(rowData),
            icon: rowData.accountDisabled ? LockOpenIcon : LockIcon,
            tooltip: t(rowData.accountDisabled ? "ENABLE_ACCOUNT" : "DISABLE_ACCOUNT"),
          }),
          ...(settings.driver_approval ? [
            (rowData: IUserType) => ({
              icon: rowData.approved ? BlockIcon : DoneAllIcon,
              tooltip: t(rowData.approved ? "reject" : "accept"),
              onClick: (_: any, driver: IUserType | IUserType[]) => {
                const drivers = Array.isArray(driver) ? driver : [driver];

                drivers.forEach(driverItem => {
                  if (driverItem.id) {
                    dispatch(
                      updateUserCar(driverItem.id, {
                        approved: !driverItem.approved,
                      })
                    );

                    setData(prevData =>
                      prevData?.map(item =>
                        item.id === driverItem.id
                          ? { ...item, approved: !driverItem.approved }
                          : item
                      )
                    );

                    if (driverItem.pushToken) {
                      RequestPushMsg(driverItem.pushToken, {
                        ios: driverItem.userPlatform == "IOS",
                        title: !driverItem.approved ? t("NEWS") : t("ATTENTION"),
                        msg: !driverItem.approved
                          ? t("YOUR_ACCOUNT_HAS_BEEN_APPROVED")
                          : t("YOUR_ACCOUNT_WAS_REJECTED"),
                      });
                    }
                  }
                });
              },
            }),
          ] : []),

          ...(settings.REACT_APP_PAGOPAR_PLANS_ACTIVE === true ? [{
            icon: 'assignment_turned_in',
            tooltip: t('ACTIVATE_PLAN_LOWER'),
            onClick: (_: any, driver: IUserType | IUserType[]) => {
              if (!Array.isArray(driver)) {
                handleOpenPlanModal(driver);
              }
            }
          }] : [])
        ]}
      />

      <ThemedDriverActivePlan
        selectedDriver={selectedUser}
        activatePlanModalOpen={activatePlanModalOpen}
        setActivatePlanModalOpen={setActivatePlanModalOpen}
      />

      <UserAccountStatusModal
        open={accountModalOpen}
        selectedUser={selectedUser}
        onClose={() => setAccountModalOpen(false)}
        onStatusChange={() => setTimeout(() => { window.location.reload() }, 1000)}
      />
    </>
  );
}